import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, ArrowRight, Loader2, AlertCircle } from "lucide-react";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import { api } from "@/lib/api";

const MAX_POLLS = 10;
const POLL_INTERVAL_MS = 2000;

export default function BookingConfirmedPage() {
  const [params] = useSearchParams();
  const stripeSessionId = params.get("session_id");
  const paypalOrderId = params.get("token"); // PayPal appends ?token=ORDER_ID
  const provider = params.get("provider");
  const sessionId = stripeSessionId || paypalOrderId;
  const isPaypal = provider === "paypal" || !!paypalOrderId;
  const [state, setState] = useState("polling"); // polling | paid | timeout | error
  const [bookingId, setBookingId] = useState(null);
  const attemptsRef = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setState("error");
      return;
    }
    let cancelled = false;

    const capturePaypalThenPoll = async () => {
      if (isPaypal) {
        try {
          const r = await api.post(`/paypal/capture/${sessionId}`);
          if (r.data.status === "paid" || r.data.status === "already_paid") {
            setBookingId(r.data.booking_id);
            setState("paid");
            return;
          }
        } catch (_) {
          // fall through to polling
        }
      }
      poll();
    };

    const poll = async () => {
      if (cancelled) return;
      attemptsRef.current += 1;
      try {
        const r = await api.get(`/payments/status/${sessionId}`);
        const st = r.data.payment_status;
        if (st === "paid") {
          setBookingId(r.data.booking_id);
          setState("paid");
          return;
        }
        if (st === "expired" || st === "failed") {
          setState("error");
          return;
        }
      } catch (_) {
        // ignore transient
      }
      if (attemptsRef.current >= MAX_POLLS) {
        setState("timeout");
        return;
      }
      setTimeout(poll, POLL_INTERVAL_MS);
    };
    capturePaypalThenPoll();

    return () => {
      cancelled = true;
    };
  }, [sessionId, isPaypal]);

  return (
    <div className="grain bg-[#050505] min-h-screen">
      <Nav />
      <main className="pt-32 md:pt-40 pb-24 min-h-[70vh]">
        <div className="max-w-[900px] mx-auto px-6 md:px-12">
          {state === "polling" && (
            <div data-testid="booking-confirming" className="py-24 text-center">
              <Loader2 className="mx-auto mb-8 animate-spin text-white" size={32} strokeWidth={1.4} />
              <p className="eyebrow mb-4">Processing Payment</p>
              <h1 className="font-serif text-4xl md:text-6xl text-white leading-tight">
                Confirming your <span className="italic text-brand-red">deposit…</span>
              </h1>
              <p className="mt-4 text-neutral-500 text-sm">
                This usually takes a couple of seconds.
              </p>
            </div>
          )}

          {state === "paid" && (
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
              data-testid="booking-confirmed"
              className="border border-white/10 bg-white/[0.02] p-10 md:p-16"
            >
              <CheckCircle2 className="text-brand-red mb-6" size={44} strokeWidth={1.2} />
              <p className="eyebrow mb-3">
                Deposit Paid · Booking Confirmed
                {bookingId ? ` · #${bookingId.slice(0, 8).toUpperCase()}` : ""}
              </p>
              <h1 className="font-serif text-5xl md:text-7xl text-white leading-[0.95]">
                Your slot is <span className="italic text-brand-red">secured.</span>
              </h1>
              <p className="mt-6 text-neutral-400 text-base md:text-lg max-w-lg leading-relaxed">
                We've received your $50 deposit and your booking is locked in. A
                confirmation email is on its way — we'll be in touch shortly to
                confirm the exact time.
              </p>

              <div className="mt-10 border-t border-white/10 pt-6 text-sm">
                <p className="eyebrow mb-3">Reminder — Booking Policy</p>
                <p className="text-neutral-400 leading-relaxed max-w-xl">
                  If you don't show up within 1 hour of your appointment (without
                  prior notice of delay over an hour or cancellation after the
                  agreed time), the deposit will be non-refundable.
                </p>
              </div>

              <div className="mt-10 flex flex-wrap gap-3">
                <Link to="/" className="btn-primary" data-testid="booking-confirmed-home">
                  Back to Home <ArrowRight size={14} />
                </Link>
                <a href="tel:0429052189" className="btn-ghost">
                  Call SEVEN7H · 0429 052 189
                </a>
              </div>
            </motion.div>
          )}

          {state === "timeout" && (
            <div className="border border-white/10 bg-white/[0.02] p-10 md:p-16">
              <AlertCircle className="text-white/70 mb-6" size={40} strokeWidth={1.2} />
              <p className="eyebrow mb-3">Still Processing</p>
              <h1 className="font-serif text-4xl md:text-6xl text-white leading-tight">
                Payment is taking longer than usual.
              </h1>
              <p className="mt-4 text-neutral-400 text-base leading-relaxed max-w-lg">
                Don't worry — your booking is saved and we'll confirm as soon as
                the payment provider finalises the transaction. You should
                receive an email shortly. If you don't, please call us on
                0429 052 189.
              </p>
              <Link to="/" className="mt-8 inline-block btn-primary">
                Back to Home <ArrowRight size={14} />
              </Link>
            </div>
          )}

          {state === "error" && (
            <div className="border border-white/10 bg-white/[0.02] p-10 md:p-16">
              <AlertCircle className="text-red-400 mb-6" size={40} strokeWidth={1.2} />
              <p className="eyebrow mb-3">Payment Not Completed</p>
              <h1 className="font-serif text-4xl md:text-6xl text-white leading-tight">
                Something went wrong.
              </h1>
              <p className="mt-4 text-neutral-400 text-base leading-relaxed max-w-lg">
                Your payment did not complete. No charge was made. You can try
                again from the booking page.
              </p>
              <Link to="/#booking" className="mt-8 inline-block btn-primary">
                Try Again <ArrowRight size={14} />
              </Link>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
